<template>
  <div class="comment">
    <router-link :to="{ name: 'profile', params: { id: comment.user.id } }">
      <avatar :user="comment.user" />
    </router-link>

    <div>
      <div class="body">
        <router-link :to="{ name: 'profile', params: { id: comment.user.id } }">
          {{ comment.user.first_name }} {{ comment.user.last_name }}
        </router-link>

        <div v-if="!editing">
          {{ comment.body }}
        </div>
        <div v-else>
          <edit-comment
            :comment="comment"
            :parent-id="objectId"
            @success="editing = !editing"
          />
        </div>

        <div
          v-if="comment.total_likes > 0"
          class="total-likes"
        >
          <total-likes
            :comment="true"
            :likes="comment.likes"
            :total-likes="comment.total_likes"
          />
        </div>
      </div>

      <div class="timestamp-and-actions small">
        <div class="text-muted">
          {{ dateFromNow(comment.created) }}
        </div>

        <template v-if="userData">
          <div>
            <like
              :comment="comment"
              :post-id="objectId"
              :parent-comment-id="parentCommentId"
            />
          </div>

          <div v-if="comment.replies !== undefined">
            <button
              type="button"
              class="btn btn-transparent"
              @click="replyClicked"
            >
              Reply
            </button>
          </div>
        </template>
      </div>
    </div>

    <item-menu
      v-if="userData"
      :comment="comment"
      :parent-id="objectId"
      @editItem="editing = true"
    />

    <div
      v-if="comment.replies !== undefined"
      class="replies"
    >
      <comment
        v-for="reply of comment.replies"
        :key="`comment${reply.id}`"
        :object-type="objectType"
        :object-id="objectId"
        :comment="reply"
        :parent-comment-id="comment.id"
        @replyClicked="replyToAReply(reply)"
      />
    </div>

    <comment-form
      v-if="showReplyForm"
      ref="replyForm"
      :object-type="objectType"
      :object-id="objectId"
      :parent-comment="replyParentComment"
      class="reply-form"
    />
  </div>
</template>

<script>
import Avatar from '@/components/Avatar.vue';
import CommentForm from '@/components/CommentForm.vue';
import EditComment from '@/components/EditComment.vue';
import ItemMenu from '@/components/ItemMenu.vue';
import Like from '@/components/Like.vue';
import TotalLikes from '@/components/TotalLikes.vue';

export default {
  name: 'Comment',
  components: {
    Avatar,
    CommentForm,
    EditComment,
    ItemMenu,
    Like,
    TotalLikes,
  },
  props: {
    objectType: {
      type: String,
      required: true,
    },
    objectId: {
      type: Number,
      required: true,
    },
    comment: {
      type: Object,
      required: true,
    },
    parentCommentId: {
      type: Number,
      required: false,
      default() {
        return null;
      },
    },
  },
  data() {
    return {
      showReplyForm: false,
      replyParentComment: this.comment,
      editing: false,
    };
  },
  methods: {
    replyClicked() {
      if (this.comment.replies !== undefined) {
        this.replyParentComment = this.comment;
        this.showReplyFormAndFocusTextarea();
      } else {
        this.$emit('replyClicked');
      }
    },
    replyToAReply(reply) {
      this.replyParentComment = reply;
      this.showReplyFormAndFocusTextarea();
    },
    showReplyFormAndFocusTextarea() {
      this.showReplyForm = true;
      this.$nextTick(() => {
        const replyTextarea = this.$refs.replyForm.$el.querySelector('.js-reply-textarea');

        if (this.isElementInViewport(replyTextarea)) {
          replyTextarea.focus();
        } else {
          replyTextarea.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });

          setTimeout(() => {
            replyTextarea.focus();
          }, 1000);
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
  .comment {
    display: grid;
    grid-gap: 0.5rem;
    grid-template-columns: auto 1fr auto;
    margin-top: 1rem;
  }

  .body {
    position: relative;
    padding: 0.25rem 0.5rem;
    background-color: var(--gray-lightest);
    border: 1px solid var(--gray-lighter);
    border-radius: 0.25rem;
  }

  .total-likes {
    position: absolute;
    right: -1em;
    bottom: -1em;
    padding: 0 0.5em;
    border: 1px solid var(--gray-lighter);
    border-radius: 1em;
    background-color: #fff;
    opacity: 0.8;

    .btn {
      font-size: 80%;
    }
  }

  .timestamp-and-actions div {
    display: inline-block;

    + div {
      margin-left: 0.75rem;
    }

    .btn {
      font-size: inherit;
    }
  }

  .replies,
  .reply-form {
    grid-column: 2 / 4;
  }

  .replies {
    margin-top: -0.75rem;
  }
</style>
